import {
  doc,
  getDoc
} from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { apiRequest } from '../apiClient';
import { CompanyInfo, UserProfile } from '../../types';
import { invalidateCompanySubCache } from './companyService';

/**
 * Fetch all sub-accounts under a given parent company via authenticated server API.
 * Uses Firebase ID token authentication (Authorization: Bearer <token>) to satisfy requireAuth.
 * Insecure client-side Firestore fallbacks have been removed to comply with firestore.rules.
 */
export async function fetchSubAccounts(parentCompanyId: string): Promise<CompanyInfo[]> {
  if (!parentCompanyId) return [];

  // Ensure Firebase Auth state is ready before making authenticated call
  if (!auth.currentUser && typeof (auth as any).authStateReady === 'function') {
    await (auth as any).authStateReady();
  }

  try {
    const data = await apiRequest<{ subAccounts: CompanyInfo[] }>('/companies/sub-accounts', {
      method: 'GET',
      params: { parentCompanyId }
    });

    if (data && Array.isArray(data.subAccounts)) {
      return data.subAccounts;
    }
    return [];
  } catch (apiErr: any) {
    console.error(`[SubAccounts] Fejl ved hentning af underkonti for parentCompanyId=${parentCompanyId}:`, apiErr.message);
    throw apiErr;
  }
}

/**
 * Fetch all companies accessible by a user.
 * Parent accounts can see and switch to their sub-accounts; sub-accounts can see sibling sub-accounts or parent.
 */
export async function fetchUserCompanies(user: UserProfile): Promise<CompanyInfo[]> {
  if (!user || !user.companyId) return [];
  const companiesMap = new Map<string, CompanyInfo>();

  try {
    // 1. Fetch current active company
    const currentSnap = await getDoc(doc(db, 'companies', user.companyId));
    if (currentSnap.exists()) {
      const currentData = { id: currentSnap.id, ...currentSnap.data() } as CompanyInfo;
      companiesMap.set(currentData.id, currentData);

      // Fetch any sub-accounts created directly under this company (main or sub)
      try {
        const children = await fetchSubAccounts(currentData.id);
        for (const child of children) {
          companiesMap.set(child.id, child);
        }
      } catch (err: any) {
        console.warn('[fetchUserCompanies] Could not fetch child sub-accounts:', err.message);
      }

      // If this is a sub-account and the user is an admin/superadmin, also allow switching to sibling sub-accounts or parent
      const isSubAccount = Boolean(currentData.parentCompanyId);
      if (isSubAccount && (user.role === 'admin' || user.superadmin || !user.role || (user as any).role === 'administrator')) {
        try {
          const parentSnap = await getDoc(doc(db, 'companies', currentData.parentCompanyId!));
          if (parentSnap.exists()) {
            companiesMap.set(parentSnap.id, { id: parentSnap.id, ...parentSnap.data() } as CompanyInfo);
          }
        } catch {
          // ignore
        }

        try {
          const siblingSubs = await fetchSubAccounts(currentData.parentCompanyId!);
          for (const sub of siblingSubs) {
            companiesMap.set(sub.id, sub);
          }
        } catch (err: any) {
          console.warn('[fetchUserCompanies] Could not fetch sibling sub-accounts:', err.message);
        }
      }
    }

    // 2. Also fetch any companies explicitly listed in user.companyIds
    if (Array.isArray(user.companyIds) && user.companyIds.length > 0) {
      for (const cId of user.companyIds) {
        if (!companiesMap.has(cId)) {
          try {
            const cSnap = await getDoc(doc(db, 'companies', cId));
            if (cSnap.exists()) {
              companiesMap.set(cSnap.id, { id: cSnap.id, ...cSnap.data() } as CompanyInfo);
            }
          } catch {
            // ignore
          }
        }
      }
    }
  } catch (err) {
    console.warn('Notice in fetchUserCompanies:', err);
  }

  return Array.from(companiesMap.values());
}

/**
 * Create a new sub-account / underkonto linked to a parent company.
 * Dispatched strictly through authenticated server API with Admin SDK (bypasses Firestore client security limits).
 * Client SDK fallback has been removed to prevent firestore.rules violations.
 */
export async function createSubAccount(
  parentCompanyId: string,
  data: {
    name: string;
    cvr: string;
    billingEmail: string;
    address?: string;
    zip?: string;
    city?: string;
    country?: string;
    brandColor?: string;
  },
  creatorUser: UserProfile
): Promise<CompanyInfo> {
  if (!parentCompanyId) {
    throw new Error('Hovedkonto ID mangler.');
  }

  // Ensure Firebase Auth state is ready before making authenticated call
  if (!auth.currentUser && typeof (auth as any).authStateReady === 'function') {
    await (auth as any).authStateReady();
  }

  const result = await apiRequest<{ success: boolean; subAccount: CompanyInfo }>('/companies/sub-accounts', {
    method: 'POST',
    body: JSON.stringify({
      parentCompanyId,
      companyData: data,
      creatorUid: creatorUser.uid
    })
  });

  if (!result || !result.subAccount) {
    throw new Error('Serveren returnerede ikke den oprettede underkonto.');
  }

  invalidateCompanySubCache(parentCompanyId);
  invalidateCompanySubCache(result.subAccount.id);

  return result.subAccount;
}

/**
 * Update an existing sub-account's information via authenticated server API.
 * Client SDK fallback removed.
 */
export async function updateSubAccount(
  subCompanyId: string,
  data: Partial<CompanyInfo>
): Promise<void> {
  if (!subCompanyId) {
    throw new Error('Underkonto ID mangler.');
  }

  invalidateCompanySubCache(subCompanyId);

  // Ensure Firebase Auth state is ready before making authenticated call
  if (!auth.currentUser && typeof (auth as any).authStateReady === 'function') {
    await (auth as any).authStateReady();
  }

  await apiRequest(`/companies/sub-accounts/${encodeURIComponent(subCompanyId)}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
}

/**
 * Check whether a company can be deleted (cannot delete a parent company if it has sub-accounts).
 */
export async function canDeleteCompany(companyId: string): Promise<{ canDelete: boolean; reason?: string }> {
  try {
    const subs = await fetchSubAccounts(companyId);
    if (subs.length > 0) {
      return {
        canDelete: false,
        reason: `Virksomheden er en hovedkonto med ${subs.length} tilknyttet(te) underkonto/underkonti. Slet eller frakobl underkontiene først.`
      };
    }
    return { canDelete: true };
  } catch (err: any) {
    return { canDelete: false, reason: err.message };
  }
}

/**
 * Delete a sub-account via authenticated server API.
 * Client SDK fallback removed.
 */
export async function deleteSubAccount(
  subCompanyId: string,
  parentCompanyId: string,
  userUid?: string
): Promise<void> {
  if (!subCompanyId) {
    throw new Error('Underkonto ID mangler.');
  }

  invalidateCompanySubCache(subCompanyId);
  if (parentCompanyId) invalidateCompanySubCache(parentCompanyId);

  // Ensure Firebase Auth state is ready before making authenticated call
  if (!auth.currentUser && typeof (auth as any).authStateReady === 'function') {
    await (auth as any).authStateReady();
  }

  await apiRequest(`/companies/sub-accounts/${encodeURIComponent(subCompanyId)}`, {
    method: 'DELETE',
    body: JSON.stringify({ parentCompanyId, userUid })
  });
}

/**
 * Link or unlink sub-account to parent company (for Superadmin or Admin) via authenticated server API.
 * Client SDK fallback removed.
 */
export async function linkSubAccountToParent(
  childCompanyId: string,
  parentCompanyId: string | null
): Promise<void> {
  if (!childCompanyId) {
    throw new Error('Underkonto ID mangler.');
  }

  // Ensure Firebase Auth state is ready before making authenticated call
  if (!auth.currentUser && typeof (auth as any).authStateReady === 'function') {
    await (auth as any).authStateReady();
  }

  await apiRequest('/companies/link-sub-account', {
    method: 'POST',
    body: JSON.stringify({ childCompanyId, parentCompanyId })
  });

  invalidateCompanySubCache(childCompanyId);
  if (parentCompanyId) invalidateCompanySubCache(parentCompanyId);
}

/**
 * Switch active company for a user via authenticated server API.
 * Client SDK fallback removed to prevent Firestore rule violations.
 */
export async function switchUserCompany(uid: string, targetCompanyId: string): Promise<void> {
  if (!uid || !targetCompanyId) {
    throw new Error('Bruger ID og Virksomheds ID påkrævet.');
  }

  // Ensure Firebase Auth state is ready before making authenticated call
  if (!auth.currentUser && typeof (auth as any).authStateReady === 'function') {
    await (auth as any).authStateReady();
  }

  await apiRequest('/companies/switch-active', {
    method: 'POST',
    body: JSON.stringify({ uid, targetCompanyId })
  });

  invalidateCompanySubCache(targetCompanyId);
}
